import { useEffect, useRef, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';

const testimonials = [
  {
    name: '张伟',
    role: '短视频创作者 / 抖音50万粉丝',
    quote: '用了萝卜视频之后，我的视频产能直接翻了三倍。以前剪一条视频要两三个小时，现在AI帮我搞定大部分工作，我只需要做最后的创意把控。AI配音的质量也出乎意料，观众根本听不出来是AI配的。',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20headshot%20of%20young%20asian%20man%20with%20warm%20friendly%20smile%2C%20clean%20studio%20background%2C%20soft%20natural%20lighting%2C%20modern%20casual%20attire%2C%20professional%20yet%20approachable%20look&width=80&height=80&seq=testimonial-1&orientation=squarish',
  },
  {
    name: '李婷',
    role: '在线教育机构课程制作负责人',
    quote: '我们机构每月需要制作100+条教学视频，萝卜视频帮我们把制作时间从每条3天缩短到4小时。AI字幕和配音功能让我们的视频质量提升了一个档次，学员满意度从86%提升到94%。强烈推荐给教育行业的同行们。',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20headshot%20of%20young%20asian%20woman%20with%20confident%20smile%2C%20clean%20studio%20background%2C%20soft%20natural%20lighting%2C%20modern%20business%20attire%2C%20approachable%20professional%20look&width=80&height=80&seq=testimonial-2&orientation=squarish',
  },
  {
    name: '王浩然',
    role: '电商卖家 / 淘宝店主',
    quote: '做电商最需要的就是快速产出高质量的产品展示视频和带货视频。萝卜视频的AI智能剪辑和模板功能太实用了，一条带货视频15分钟就能搞出来。模板库里的电商模板很专业，帮我省了不少找外包的钱。',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20headshot%20of%20asian%20man%20in%20his%20thirties%20with%20friendly%20expression%2C%20clean%20studio%20background%2C%20soft%20natural%20lighting%2C%20business%20casual%20attire%2C%20warm%20professional%20look&width=80&height=80&seq=testimonial-3&orientation=squarish',
  },
  {
    name: '陈晓萌',
    role: 'MCN机构内容总监',
    quote: '管理20多个创作者的视频内容，协调效率是最大的挑战。萝卜视频的团队协作版解决了这个问题，所有人可以在同一个项目里协作，版本管理非常清晰。AI批量处理功能也让我们的内容生产效率大幅提升。',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20headshot%20of%20young%20asian%20woman%20with%20bright%20smile%2C%20clean%20studio%20background%2C%20soft%20natural%20lighting%2C%20modern%20casual%20style%2C%20creative%20professional%20look&width=80&height=80&seq=testimonial-4&orientation=squarish',
  },
];

const stats = [
  { value: '500,000+', label: '注册创作者' },
  { value: '1,200万+', label: '视频制作完成' },
  { value: '98.5%', label: '用户满意度' },
  { value: '50+', label: '支持语言' },
];

export default function AboutPage() {
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formResult, setFormResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const contactFormUrl = 'https://readdy.ai/api/form/d9rifvee3oq4jqumu1p0';

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!contactFormUrl) return;

    const formEl = e.currentTarget;
    const formData = new FormData(formEl);
    const honeypot = formData.get('contact_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormResult({ type: 'success', message: '提交成功！我们会尽快与您联系。' });
      formEl.reset();
      return;
    }
    formData.delete('contact_alt');

    setFormSubmitting(true);
    setFormResult(null);

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, String(value));
      });

      const res = await fetch(contactFormUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      if (res.ok && parsed?.code === 'OK') {
        setFormResult({ type: 'success', message: '提交成功！我们会尽快与您联系。' });
        formEl.reset();
      } else {
        const serverMsg =
          (parsed?.meta as Record<string, unknown>)?.message ||
          (parsed?.meta as Record<string, unknown>)?.detail ||
          (parsed?.message as string) ||
          responseText ||
          '提交失败，请稍后重试。';
        setFormResult({ type: 'error', message: String(serverMsg) });
      }
    } catch {
      setFormResult({ type: 'error', message: '网络错误，请稍后重试。' });
    } finally {
      setFormSubmitting(false);
    }
  };

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于萝卜视频 - 智能AI视频创作平台',
    description: '萝卜视频是一款专为创作者打造的智能视频工具，致力于让每个人都能轻松制作专业级视频内容。了解我们的品牌故事、团队背景和联系方式。',
    url: 'https://luoboshipin.com/about',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://luoboshipin.com/' },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://luoboshipin.com/about' },
      ],
    },
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 萝卜视频 | 智能AI视频创作平台"
        description="萝卜视频致力于让每个人都能轻松制作专业级视频内容。了解我们的品牌故事、团队背景、用户口碑和联系方式。"
        keywords="萝卜视频,关于萝卜视频,AI视频工具,视频创作平台,联系我们"
        canonicalUrl="https://luoboshipin.com/about"
        schemaData={schemaData}
      />

      <Navbar />

      <main className="pt-20 bg-background-50">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb items={[{ name: '首页', path: '/' }, { name: '关于我们' }]} />

            <section className="mt-6 mb-16">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                关于萝卜视频
              </h1>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <p className="text-base text-foreground-700 leading-relaxed mb-4">
                    萝卜视频诞生于2024年，由一群热爱视频创作的技术专家和内容创作者共同创立。我们的使命是让视频创作不再有门槛——无论你是专业剪辑师还是零基础的创作新手，都能用萝卜视频轻松制作出专业级的视频内容。
                  </p>
                  <p className="text-base text-foreground-700 leading-relaxed mb-4">
                    我们深知传统视频制作流程的痛点：学习成本高、制作周期长、专业软件价格昂贵。因此，我们投入大量研发资源，将最前沿的AI技术与视频创作场景深度融合，打造出一款真正"懂创作者"的智能工具。
                  </p>
                  <p className="text-base text-foreground-700 leading-relaxed">
                    截至目前，萝卜视频已服务超过<strong>50万</strong>创作者，累计完成<strong>1200万+</strong>条视频的制作。我们的用户遍布短视频创作、在线教育、电商带货、企业营销等多个领域，用AI技术帮助他们实现了创作效率和内容质量的双重飞跃。
                  </p>
                </div>
                <div className="rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20space%20with%20warm%20orange%20and%20wood%20accents%2C%20collaborative%20team%20working%20environment%2C%20large%20windows%20with%20natural%20light%2C%20professional%20studio%20atmosphere%2C%20minimalist%20modern%20interior%20design%2C%20creative%20tech%20company%20vibe%2C%20warm%20inviting%20workspace&width=600&height=400&seq=about-team&orientation=landscape"
                    alt="萝卜视频团队办公环境"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </section>

            <section className="mb-16">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                {stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="text-center p-6 rounded-lg bg-background-100 border border-background-200/70"
                  >
                    <div className="text-3xl font-heading font-bold text-primary-500 mb-2">{stat.value}</div>
                    <div className="text-sm text-foreground-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-10 text-center">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {testimonials.map((t) => (
                  <div
                    key={t.name}
                    className="p-6 rounded-lg bg-background-100 border border-background-200/70"
                  >
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                        <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                        <div className="text-xs text-foreground-500">{t.role}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-400 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{t.quote}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-10 text-center">
                联系我们
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-mail-line text-lg"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-900 mb-1">商务合作</h4>
                        <p className="text-sm text-foreground-600">business@luoboshipin.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                        <i className="ri-customer-service-2-line text-lg"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-900 mb-1">客服支持</h4>
                        <p className="text-sm text-foreground-600">support@luoboshipin.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                        <i className="ri-map-pin-line text-lg"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h4>
                        <p className="text-sm text-foreground-600">中国北京市海淀区中关村科技园创新大厦18层</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-lg bg-background-100 border border-background-200/70">
                  <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-5">发送消息</h3>
                  <form ref={formRef} onSubmit={handleContactSubmit} data-readdy-form="">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                          姓名
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-white text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                          邮箱
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-white text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                          消息内容
                        </label>
                        <textarea
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-white text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all resize-none"
                          placeholder="请输入您的消息（最多500字）"
                        ></textarea>
                      </div>
                      <div className="honeypot-field">
                        <input
                          type="text"
                          name="contact_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={formSubmitting}
                        className="w-full px-6 py-3 rounded-md bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                      >
                        {formSubmitting ? '提交中...' : '发送消息'}
                      </button>
                      {formResult && (
                        <p
                          className={`text-sm text-center ${
                            formResult.type === 'success' ? 'text-accent-600' : 'text-red-500'
                          }`}
                        >
                          {formResult.message}
                        </p>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}